import React from "react";
import type { ExportableAssistantMessage } from "./Utils/getExportableConversation";

export interface AssistantConversationPdfProps {
    eventTitle: string;
    messages: ExportableAssistantMessage[];
    documentTitle?: string;
    emptyStateText?: string;
}

export type { ExportableAssistantMessage } from "./Utils/getExportableConversation";

export type AssistantConversationPdfBlock =
    | {
          type: "text";
          key: string;
          variant: "title" | "eventTitle" | "role" | "body" | "emptyState";
          text: string;
      }
    | {
          type: "divider";
          key: string;
      };

export const ASSISTANT_CONVERSATION_PDF_PAGE = {
    width: 595.28,
    height: 841.89,
    paddingTop: 52,
    paddingRight: 54,
    paddingBottom: 56,
    paddingLeft: 54,
} as const;

const DEFAULT_DOCUMENT_TITLE = "AI Analyst Conversation";
const DEFAULT_EMPTY_STATE_TEXT =
    "No conversation messages available to export.";

export const ASSISTANT_CONVERSATION_PDF_COLORS = {
    title: "#1a2744",
    subtitle: "#4e5f80",
    role: "#203556",
    body: "#4f5b6e",
    divider: "#d8e0ec",
    page: "#ffffff",
} as const;

export const ASSISTANT_CONVERSATION_PDF_TEXT_STYLES = {
    title: {
        fontSize: 22,
        lineHeight: 1.25,
    },
    eventTitle: {
        fontSize: 11,
        lineHeight: 1.5,
    },
    role: {
        fontSize: 11,
        lineHeight: 1.4,
    },
    body: {
        fontSize: 11.5,
        lineHeight: 1.75,
    },
    emptyState: {
        fontSize: 11.5,
        lineHeight: 1.7,
    },
} as const;

export const ASSISTANT_CONVERSATION_PDF_SPACING = {
    eventTitleTopMargin: 10,
    roleBottomMargin: 10,
    sectionDividerMargin: 22,
} as const;

const sanitizeText = (value: string): string => value.replace(/\r\n/g, "\n");

const normalizeEventTitle = (value: string): string => value.trim();

const normalizeMessages = (
    messages: ExportableAssistantMessage[]
): ExportableAssistantMessage[] =>
    messages.filter(
        (message) =>
            typeof message?.text === "string" && message.text.trim().length > 0
    );

export const createAssistantConversationPdfBlocks = ({
    eventTitle,
    messages,
    documentTitle,
    emptyStateText,
}: AssistantConversationPdfProps): AssistantConversationPdfBlock[] => {
    const normalizedDocumentTitle = (documentTitle || "").trim();
    const normalizedEmptyStateText = (emptyStateText || "").trim();

    const blocks: AssistantConversationPdfBlock[] = [
        {
            type: "text",
            key: "document-title",
            variant: "title",
            text: normalizedDocumentTitle || DEFAULT_DOCUMENT_TITLE,
        },
    ];
    const normalizedEventTitle = normalizeEventTitle(eventTitle);
    if (normalizedEventTitle) {
        blocks.push({
            type: "text",
            key: "event-title",
            variant: "eventTitle",
            text: normalizedEventTitle,
        });
    }

    blocks.push({ type: "divider", key: "document-header-divider" });

    const conversationMessages = normalizeMessages(messages);
    if (conversationMessages.length === 0) {
        blocks.push({
            type: "text",
            key: "empty-state",
            variant: "emptyState",
            text: normalizedEmptyStateText || DEFAULT_EMPTY_STATE_TEXT,
        });
        return blocks;
    }

    conversationMessages.forEach((message, index) => {
        blocks.push({
            type: "text",
            key: `role-${index}`,
            variant: "role",
            text: message.role,
        });
        blocks.push({
            type: "text",
            key: `body-${index}`,
            variant: "body",
            text: sanitizeText(message.text),
        });

        if (index < conversationMessages.length - 1) {
            blocks.push({
                type: "divider",
                key: `divider-${index}`,
            });
        }
    });

    return blocks;
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        width: ASSISTANT_CONVERSATION_PDF_PAGE.width,
        minHeight: ASSISTANT_CONVERSATION_PDF_PAGE.height,
        padding: `${ASSISTANT_CONVERSATION_PDF_PAGE.paddingTop}px ${ASSISTANT_CONVERSATION_PDF_PAGE.paddingRight}px ${ASSISTANT_CONVERSATION_PDF_PAGE.paddingBottom}px ${ASSISTANT_CONVERSATION_PDF_PAGE.paddingLeft}px`,
        boxSizing: "border-box",
        backgroundColor: ASSISTANT_CONVERSATION_PDF_COLORS.page,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.body,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
    },
    title: {
        margin: 0,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.title,
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.title.fontSize,
        fontWeight: 700,
        lineHeight: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.title.lineHeight,
        letterSpacing: 0.2,
    },
    eventTitle: {
        margin: `${ASSISTANT_CONVERSATION_PDF_SPACING.eventTitleTopMargin}px 0 0`,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.subtitle,
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.eventTitle.fontSize,
        fontWeight: 600,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.eventTitle.lineHeight,
        textTransform: "uppercase",
        letterSpacing: 1.1,
    },
    role: {
        margin: `0 0 ${ASSISTANT_CONVERSATION_PDF_SPACING.roleBottomMargin}px`,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.role,
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.role.fontSize,
        fontWeight: 700,
        lineHeight: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.role.lineHeight,
        letterSpacing: 0.9,
        textTransform: "uppercase",
    },
    body: {
        margin: 0,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.body,
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.body.fontSize,
        fontWeight: 400,
        lineHeight: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.body.lineHeight,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },
    emptyState: {
        margin: 0,
        color: ASSISTANT_CONVERSATION_PDF_COLORS.body,
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.emptyState.fontSize,
        fontWeight: 400,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.emptyState.lineHeight,
    },
    divider: {
        border: 0,
        borderTop: `1px solid ${ASSISTANT_CONVERSATION_PDF_COLORS.divider}`,
        margin: `${ASSISTANT_CONVERSATION_PDF_SPACING.sectionDividerMargin}px 0`,
    },
};

const renderTextBlock = (
    block: Extract<AssistantConversationPdfBlock, { type: "text" }>
) => {
    switch (block.variant) {
        case "title":
            return (
                <h1 key={block.key} style={styles.title}>
                    {block.text}
                </h1>
            );
        case "eventTitle":
            return (
                <p key={block.key} style={styles.eventTitle}>
                    {block.text}
                </p>
            );
        case "role":
            return (
                <p key={block.key} style={styles.role}>
                    {block.text}
                </p>
            );
        case "emptyState":
            return (
                <p key={block.key} style={styles.emptyState}>
                    {block.text}
                </p>
            );
        case "body":
        default:
            return (
                <p key={block.key} style={styles.body}>
                    {block.text}
                </p>
            );
    }
};

export const AssistantConversationPdf: React.FC<
    AssistantConversationPdfProps
> = ({ eventTitle, messages, documentTitle, emptyStateText }) => {
    const blocks = createAssistantConversationPdfBlocks({
        eventTitle,
        messages,
        documentTitle,
        emptyStateText,
    });

    return (
        <article
            aria-label={documentTitle?.trim() || DEFAULT_DOCUMENT_TITLE}
            style={styles.page}
        >
            {blocks.map((block) =>
                block.type === "divider" ? (
                    <hr key={block.key} style={styles.divider} />
                ) : (
                    renderTextBlock(block)
                )
            )}
        </article>
    );
};
